import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useHasPermission } from '@/hooks/use-has-permission';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useCurrentUserId } from '@/modules/service-desk/shared/employee';
import { mergeWhere } from '@/lib/table-query';
import {
  ApplicationsTable,
  useApplicationsTable,
} from '@/modules/service-desk/shared/components/applications-table';
import {
  useIdCardApplications,
  useIdCardFileUrls,
  useWithdrawIdCard,
} from '../../hooks/use-id-card';
import { IdCardApplication } from '../../types/id-card.types';
import { createMyIdCardColumns } from '../id-card-columns/id-card-columns';
import { useDepartmentOptions } from '@/modules/shared/hr-lookups/hooks/use-lookups';
import type { DepartmentLookupOption } from '@/modules/shared/hr-lookups/types/lookup.types';

const NO_DEPARTMENTS: DepartmentLookupOption[] = [];

export const IdCardMyApplications = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userId = useCurrentUserId();
  const canApply = useHasPermission(HRM_PERMISSIONS.ID_CARD_APPLICATION.APPLY);
  const canWithdraw = useHasPermission(HRM_PERMISSIONS.ID_CARD_APPLICATION.WITHDRAW);
  const table = useApplicationsTable();
  // Every department, only to name the applicant's own on the phone card.
  const { data: departments = NO_DEPARTMENTS } = useDepartmentOptions();
  const [withdrawTarget, setWithdrawTarget] = useState<IdCardApplication | null>(null);

  const { data, isLoading, error } = useIdCardApplications(
    {
      pageNo: table.pagination.pageIndex + 1,
      pageSize: table.pagination.pageSize,
      filter: mergeWhere({ UserId: userId }, table.columnWhere),
      order: table.order,
    },
    Boolean(userId)
  );
  const { data: fileUrls = {} } = useIdCardFileUrls(data?.items ?? []);
  const { mutateAsync: withdraw } = useWithdrawIdCard();

  const columns = useMemo(
    () =>
      createMyIdCardColumns({
        t,
        departments,
        fileUrls,
        canApply,
        canWithdraw,
        onEdit: (application) =>
          navigate(`/hr/service-desk/id-card/apply?id=${application.ItemId}`),
        onWithdraw: setWithdrawTarget,
      }),
    [t, departments, fileUrls, canApply, canWithdraw, navigate]
  );

  return (
    <>
      <ApplicationsTable
        columns={columns}
        result={data}
        isLoading={isLoading}
        error={error}
        table={table}
      />
      <ConfirmationModal
        open={Boolean(withdrawTarget)}
        onOpenChange={(open) => !open && setWithdrawTarget(null)}
        title={t('WITHDRAW_ID_CARD')}
        confirmText={t('WITHDRAW')}
        description={t('ARE_YOU_SURE_TO_WITHDRAW_ID_CARD')}
        onConfirm={() => withdrawTarget && withdraw(withdrawTarget.ItemId)}
      />
    </>
  );
};
